// Every user-visible string on the landing page, in one place.
//
// fancy-nimbus asks for a strings map so the page can be rendered twice, once
// per language, from one component tree. The English half is not written yet
// and `/en` does not exist, so this is the French map on its own - shaped so
// adding `en` is a second key rather than a refactor.
//
// Nothing here is a claim the repo cannot back:
//
//  * Every ask in CAN_ASK maps to a tool the agent actually has -
//    search_tournaments, get_tournament, find_available_partners,
//    get_player_ranking, search_clubs, find_my_games, propose_availability.
//    Checked against internal/agent, not written from imagination.
//  * Every line in LIMITS is a rule enforced in the agent's system prompt
//    (internal/agent/agent.go:72-80), quoted closely enough to stay true.
//  * There is no price on this page. fancy-nimbus flagged "gratuit" as a
//    promise nobody has confirmed, so it is not made here.
//  * Court booking IS mentioned now, and carries a qualifier that keeps it
//    true. It was off this page entirely while it was unreachable - one `if`
//    in SearchView.swift made it invisible to everybody - and fancy-nimbus
//    left it out for that reason. It works now, but it is still gated on the
//    PISTAS flag at SearchView.swift:232, it exists on iOS only (ui/android
//    has no booking code), and a slot only appears in a club a player has
//    favourited AND an operator provisioned with courts. The note on the
//    SCREENS.areas card is those three facts compressed; silver-zephyr-3 has
//    the full reading.

export const NAV = {
  assistant: "L'assistant",
  features: 'Fonctionnalités',
  support: 'Support',
}

export const HERO = {
  eyebrow: 'Assistant de jeu',
  heading: 'Demandez. Il cherche. Vous décidez.',
  lede:
    "Padelr est une application pour les joueurs de padel français. Posez votre question en une phrase — l'assistant interroge les tournois, les classements, vos contacts et vos communautés, et vous répond.",
  note: 'Disponible sur iOS et Android.',
}

// The hero exchange, as a script the hero card plays out. Illustrative and
// labelled as such, per lively-jaguar.
//
// It used to be four static turns. quiet-orbit-3 asked for the hero to show
// what the agent *does*, so each scene now carries the tool calls between the
// question and the answer - and that is where honesty has to hold, because a
// tool call on screen is a claim. Every `tool` below is a tool registered in
// internal/agent/tools.go, called in the order the agent really calls them:
// propose_availability's own description tells the model to call
// get_current_date first, so the second scene does too.
//
// The reader sees `name` and `target`, never `tool`. The first version showed
// the identifiers themselves - search_tournaments, get_current_date - and on
// a page for French players that read as English code in the middle of the
// exchange. So each call says in French what the assistant is doing, and the
// identifier stays here, where heroTimeline.test.js checks it against the
// agent.
//
// The third turn of the first scene carries the subject over from the answer
// before it ("celui du 19"), which is precisely what a screen full of filters
// cannot do. The tournaments are plausible rather than real, which is why the
// card says so out loud.
//
// The second scene ends on the card the app really shows. propose_availability
// changes nothing - it returns a proposal the player confirms by tapping - so
// the answer says it is a proposal, and the card is what gets confirmed. That
// is "Rien ne change sans vous" from TRUST, shown happening.
//
// Timings live in AnimatedConversation.js, not here: this is what is said,
// that is how fast.
export const HERO_SCRIPT = {
  title: 'Assistant Padelr',
  badge: "échange d'exemple",
  footnote:
    'Les tournois cités sont fictifs. L’assistant ne répond que sur des tournois réellement renvoyés par une recherche.',
  composerPlaceholder: 'Posez votre question…',
  pause: 'Mettre l’animation en pause',
  play: 'Reprendre l’animation',
  // Heads a group of two or more tool calls. A single call has no heading.
  toolsLabel: (n) => `${n} outils consultés`,
  // Who says what, in the transcript given to screen readers.
  speakers: { user: 'Vous', assistant: 'Assistant', tools: 'L’assistant consulte' },
  scenes: [
    [
      {
        type: 'user',
        text: 'Je cherche un P100 en mixte dans les 3 prochaines semaines, pas trop loin.',
      },
      {
        type: 'tools',
        calls: [
          { tool: 'get_current_date', name: 'Vérifie la date du jour' },
          { tool: 'search_tournaments', name: 'Cherche les tournois', target: 'P100 mixte' },
        ],
      },
      {
        type: 'assistant',
        text:
          "J'en trouve trois autour de chez vous : le 12 au Padel Club Lyon, le 19 à Villeurbanne et le 26 à Bron. Les trois sont en DX P100.",
      },
      { type: 'user', text: 'Et celui du 19, il coûte combien ?' },
      {
        type: 'tools',
        calls: [{ tool: 'get_tournament', name: 'Ouvre le tournoi', target: 'Villeurbanne, le 19' }],
      },
      {
        type: 'assistant',
        text:
          "40 € par équipe, inscriptions jusqu'au 15. Quatre courts couverts, tableau de 16 équipes.",
      },
    ],
    [
      { type: 'user', text: 'Je suis dispo samedi matin.' },
      {
        type: 'tools',
        calls: [
          { tool: 'get_current_date', name: 'Vérifie la date du jour' },
          { tool: 'propose_availability', name: 'Prépare une proposition', target: 'samedi matin' },
        ],
      },
      {
        type: 'assistant',
        text:
          'Je vous propose samedi matin. Rien n’est enregistré tant que vous ne l’avez pas confirmé.',
      },
      {
        type: 'proposal',
        title: 'Disponible samedi',
        detail: 'Le matin',
        confirm: 'Confirmer',
        confirmed: 'Confirmé — vos contacts vous voient disponible.',
      },
    ],
  ],
}

export const CAN_ASK = {
  heading: 'Ce que vous pouvez lui demander',
  lede:
    "Chaque question ci-dessous correspond à un outil dont l'assistant dispose réellement. Ce qu'il ne sait pas faire, il le dit.",
  items: [
    // Four of the seven examples below are the app's own suggested questions,
    // read off the assistant's opening screen rather than invented: "Quels
    // tournois je peux jouer le mois prochain ?", "Qui est dispo jeudi soir
    // ?", "Je suis dispo ce week-end", "C'est quoi mon classement ?". Using
    // the real ones is better than writing plausible ones - a reader who
    // installs the app meets the same four on the first screen.
    {
      title: 'Trouver un tournoi',
      example: '« Quels tournois je peux jouer le mois prochain ? »',
      body:
        "Niveau, catégorie, période et distance dans une seule phrase. Ce que vous ne précisez pas est repris de vos préférences enregistrées.",
    },
    {
      title: 'Lire un tournoi en entier',
      example: '« Celui du 19, il coûte combien ? »',
      body:
        'Tarif, courts, catégories, dates d’inscription. Il garde le fil d’une question à l’autre : pas besoin de tout répéter.',
    },
    {
      title: 'Savoir qui est disponible',
      example: '« Qui est dispo jeudi soir ? »',
      body:
        'Parmi vos contacts et les membres de vos communautés. L’assistant précise comment vous connaissez chaque joueur.',
    },
    {
      title: 'Consulter un classement',
      example: '« C’est quoi mon classement ? »',
      body:
        'Votre rang actuel et le mois de publication, par joueur, par club ou par genre.',
    },
    {
      title: 'Chercher un club',
      example: '« Les clubs autour de Villeurbanne »',
      body:
        'Par nom, par ville ou par code postal : où il est, combien de courts, combien de joueurs classés.',
    },
    {
      title: 'Vous déclarer disponible',
      example: '« Je suis dispo ce week-end »',
      body:
        'La seule chose qu’il peut changer, et seulement en la proposant : une carte s’affiche, vous la validez, et vos contacts vous voient libre.',
    },
    {
      title: 'Retrouver vos parties',
      example: '« J’ai quoi de prévu cette semaine ? »',
      body:
        'Les parties que vous organisez, celles où vous jouez et celles de vos cercles.',
    },
  ],
}

// This was "Ce qu'il ne fait pas": four negative headings in a band of their
// own, given equal weight to the section above, per lively-jaguar.
//
// That framing was questioned in review and the challenge held up. Two of the
// four were already said elsewhere on the page - "il ne modifie rien tout
// seul" is the "Vous déclarer disponible" card almost word for word, and "il
// n'invente pas" is the asks' own lede plus the footnote under the hero
// conversation. A third, "il ne donne pas de chiffre qu'il n'a pas", is a real
// rule but too far into the weeds for somebody deciding whether to install an
// app. And four consecutive sentences starting "Il ne" read as an apology
// rather than as confidence.
//
// What survives is the quarter that was genuinely load-bearing and happened to
// be pointing the wrong way: "il ne voit que votre cercle" is a privacy
// guarantee, not a limitation, and saying it as one was selling it short.
//
// So the substance is kept and the framing inverted - three things a reader
// can count on, stated as such. Every one is still a rule enforced in the
// agent's system prompt; none of them became a softer claim on the way.
export const TRUST = {
  heading: 'Ce sur quoi vous pouvez compter',
  lede:
    "Ce ne sont pas des intentions : ces trois règles sont écrites dans les instructions de l'assistant.",
  // The app warns about this on the assistant's own opening screen, in an
  // orange banner, before a player types anything. The landing page said
  // nothing about it, which meant the page was making a more confident claim
  // than the product does. Paraphrased rather than quoted: the app's wording
  // ("pas encore top 100 de l'efficacité") is a ranking pun that works beside
  // a padel ranking and reads as a typo out here.
  beta:
    "L'assistant est en bêta. Vérifiez les informations importantes avant de vous inscrire à un tournoi.",
  items: [
    {
      title: 'Votre cercle, et personne d’autre',
      body:
        'Il voit vos contacts et les membres de vos communautés. Pas tout votre club, personne en dehors.',
    },
    {
      title: 'Des réponses vérifiables',
      body:
        'Il ne cite que des tournois, des classements et des profils qu’une recherche lui a réellement renvoyés. Quand il ne trouve rien, il le dit.',
    },
    {
      title: 'Rien ne change sans vous',
      body:
        'La seule action qui modifie quelque chose est une proposition : une carte s’affiche, et c’est vous qui la validez.',
    },
  ],
}

// "porte d'entrée, pas un péage" was a literal translation of lively-jaguar's
// English - "a way in, not a toll gate" - and it does not survive the trip. A
// péage is a motorway booth, which is not a metaphor French reaches for here,
// and "porte d'entrée" is a front door rather than a route in. "Passage
// obligé" carries the whole of what the English meant and is what a French
// speaker would actually say.
export const SCREENS = {
  heading: 'Et les écrans sont toujours là',
  body:
    "L'assistant est un raccourci, pas un passage obligé. Les tournois, le calendrier de disponibilités, les parties, les classements et les communautés restent accessibles écran par écran, exactement comme avant.",
  // Two real captures from the iOS app, not mockups.
  //
  // The second one was a player's FFT ranking and has been replaced. It
  // carried a named individual, their club and their rank onto a public
  // marketing page - a real person's data used to sell the product, with no
  // sign anyone had asked them. The tournament calendar says the same thing
  // about the app and shows only what is already public: club names,
  // categories, entry fees.
  //
  // Worth keeping as a rule rather than a one-off fix. A screenshot of a
  // working app is a screenshot of somebody's data unless it is a seeded
  // demo account, and "it is only a first name" is a judgement to make
  // deliberately rather than by not noticing. The remaining capture shows an
  // empty assistant screen with nothing personal in it at all.
  shots: [
    {
      src: '/img/app-assistant.webp',
      alt: "L'écran d'accueil de l'assistant dans l'application Padelr, avec quatre questions suggérées",
      caption: "L'assistant, à l'ouverture",
      note: 'Les quatre questions proposées sont celles reprises ci-dessus.',
    },
    {
      src: '/img/app-tournois.webp',
      alt: "Le calendrier des tournois dans l'application Padelr : un mois, les journées qui comptent des tournois, et la liste des épreuves",
      caption: 'Le calendrier des tournois',
      note: 'Le mois, les journées qui comptent une épreuve, et le détail de chacune.',
    },
  ],
  areas: [
    {
      title: 'Tournois',
      body:
        'Les tournois FFT, les inscriptions, les tableaux et vos résultats, à jour.',
    },
    {
      title: 'Disponibilités',
      body:
        'Dites quand vous êtes libre, voyez qui l’est aussi, et montez une partie.',
    },
    {
      title: 'Communautés',
      body:
        'Vos cercles, vos équipes et vos clubs, avec les discussions qui vont avec.',
    },
    // Court booking lands here rather than in CAN_ASK, and that is the whole
    // decision in this change.
    //
    // CAN_ASK promises in its own lede that every card maps to a tool the
    // assistant really has. internal/agent/tools.go registers nine, and none
    // of them reserves a court - so a "Réserve-moi un terrain jeudi" card
    // would be the first false claim on this page, and the most obvious one
    // for a reader to go and try. This section is about screens you tap,
    // which is exactly what booking is.
    //
    // Each clause of the body is carrying a fact rather than softening one:
    //
    //  * "vos clubs", not "les clubs" - courtSlotService.List only ever reads
    //    clubs the player has favourited.
    //  * "votre partie réserve le terrain" - booking is not standalone. The
    //    reservation is created with the game and the game holds its id
    //    (electric-marten-2 option 1), so a reader who expects a standalone
    //    "book a court" button would be surprised by the real screen.
    //
    // And the note is three separate gaps, in the order a reader cares:
    // PISTAS is granted per player, the screens are iOS-only, and only
    // operator-provisioned clubs have court records at all. "sur iOS" cannot
    // come out of it - HERO.note says "Disponible sur iOS et Android" and the
    // download band offers both stores, so without it this card sells an
    // Android reader a screen their app does not have.
    {
      title: 'Réserver un terrain',
      body:
        'Les créneaux libres de vos clubs, court par court : vous en choisissez un et votre partie réserve le terrain.',
      note: 'En cours de déploiement, sur iOS, dans les clubs équipés par Padelr.',
    },
  ],
}

export const DOWNLOAD = {
  heading: 'Télécharger Padelr',
  body: "Sur l'App Store et Google Play.",
}

export const FOOTER = {
  tagline: 'Padelr — l’application des joueurs de padel français.',
  columns: [
    {
      title: 'Produit',
      links: [
        { label: "L'assistant", href: '#assistant' },
        { label: 'Fonctionnalités', href: '#fonctionnalites' },
        { label: 'Télécharger', href: '#telecharger' },
      ],
    },
    {
      title: 'Assistance',
      links: [
        { label: 'Support', href: '/support' },
        { label: 'contact at padelr dot app', href: 'mailto:contact@padelr.app' },
      ],
    },
    {
      title: 'Légal',
      links: [{ label: 'Confidentialité', href: '/privacy' }],
    },
  ],
}
